import React, { useRef, useState } from 'react';
import { Camera, Upload, Loader2, User } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePhotoUpload = ({ 
    currentPhoto, 
    token, 
    onPhotoUpdate,
    disabled = false 
}) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        // Validate file size (max 25MB)
        if (file.size > 25 * 1024 * 1024) {
            toast.error('Image must be less than 25MB');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(
                `${API_URL}/api/athletes/upload-photo`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.url) {
                onPhotoUpdate(response.data.url);
                toast.success('Photo uploaded successfully!');
            }
        } catch (error) {
            console.error('Photo upload failed:', error);
            toast.error('Failed to upload photo');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="relative">
            <div className="w-32 h-32 md:w-40 md:h-40 bg-[#0a1628] border-2 border-primary/30 overflow-hidden">
                {currentPhoto ? (
                    <img 
                        src={currentPhoto} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        data-testid="profile-photo"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-muted-foreground" />
                    </div>
                )}
            </div>
            
            {!disabled && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        data-testid="photo-input"
                    />
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute -bottom-2 -right-2 bg-primary hover:bg-cyan-400 text-[#0a1628] w-10 h-10 p-0"
                        data-testid="upload-photo-btn"
                    >
                        {uploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Camera className="w-4 h-4" />
                        )}
                    </Button>
                </>
            )}
        </div>
    );
};

export default ProfilePhotoUpload;
